export default {
  // Page titles
  title: 'Leases',
  new_lease: 'New Lease',
  edit_lease: 'Edit Lease',
  lease_detail: 'Lease Details',

  // Descriptions
  description: 'Manage rental agreements',
  new_description: 'Create a new rental agreement',
  edit_description: 'Update lease details',

  // Labels
  labels: {
    lease_code: 'Lease Code',
    customer: 'Customer',
    unit: 'Unit',
    property: 'Property',
    start_date: 'Start Date',
    end_date: 'End Date',
    end_date_optional: 'End Date (optional)',
    deposit: 'Deposit',
    // EP-156 (T156-010, §3.3) — two optional, invoicing-independent charges.
    move_in_fee: 'Move-in Fee',
    keycard_deposit: 'Key-card Deposit',
    monthly_rent: 'Monthly Rent',
    rent: 'Rent',
    // F10/AC-8 (EP-158): the unit/rent amount used to be suffixed with the
    // hardcoded English "/mo" on Thai screens. Mirrored here unchanged.
    per_month: '/mo',
    pinned_at_activation: 'Pinned',
    status: 'Status',
    signed_at: 'Signed At',
    tenant: 'Tenant',
    phone: 'Phone',
    id_card: 'ID Card',
    address: 'Address',
  },

  // Placeholders
  placeholders: {
    select_customer: 'Select a customer',
    select_property: 'Select a property',
    select_unit: 'Select a unit',
    search_customers: 'Search customers...',
    search_properties: 'Search properties...',
    // EP-156 (T156-010): ฿200 is only the key-card placeholder, not a default.
    keycard_deposit_example: 'e.g. 200',
  },

  // Status
  status: {
    draft: 'Draft',
    pending_signatures: 'Pending Signatures',
    pending_start: 'Pending Start',
    active: 'Active',
    pending: 'Pending',
    ended: 'Ended',
    all: 'All Statuses',
    // EP-97: default live roster (active + pending_start + pending_signatures).
    live_default: 'Live leases',
    showing_live: 'Showing live leases',
  },

  // Signatures
  signatures: {
    title: 'Signatures',
    landlord: 'Landlord Signature',
    tenant: 'Tenant Signature',
    sign_lease: 'Sign Lease',
    complete_signing: 'Complete Signing',
    sign_info: 'Both the landlord and tenant must provide their signatures to complete the lease agreement.',
    both_captured: 'Both signatures captured. Ready to complete signing.',
    signed_on: 'Signed on {{date}}',
  },

  // Actions
  actions: {
    add_lease: 'New Lease',
    create_lease: 'Create Lease',
    activate: 'Activate',
    activate_lease: 'Activate Lease',
    end_lease: 'End Lease',
    download_pdf: 'Download PDF',
    generate_pdf: 'Generate PDF',
    preview_pdf: 'Preview PDF',
    generate_sign_links: 'Generate Sign Links',
    copy_link: 'Copy Link',
    bill_deposit: 'Bill Deposit',
    renew_lease: 'Renew Lease',
  },

  // Empty states
  empty: {
    title: 'No leases found',
    description: 'Start by creating your first lease.',
    no_vacant_units: 'No vacant units available in this property',
  },

  // Setup guide
  setup_guide: {
    title: 'Welcome to Leases',
    description:
      'Leases connect customers to units. Before creating a lease, make sure you have at least one property with units and one customer.',
    prerequisites_title: 'Before you begin',
    prereq_property: 'At least one property with units',
    prereq_customer: 'At least one customer',
    prereq_setup_property: 'Set up',
    prereq_add_customer: 'Add',
    step_1_title: 'Select a unit',
    step_1_description: 'Choose a vacant unit from your property.',
    step_2_title: 'Select a customer',
    step_2_description: 'Pick the tenant who will rent the unit.',
    step_3_title: 'Set lease dates and deposit',
    step_3_description:
      'Define start date, optional end date, and security deposit amount.',
    import: 'Import',
    import_off_scope_warning: 'Found {{count}} {{entity}} rows. Import them from the {{Entity}} page.',
    download_sample: 'Download sample',
    seed_sample: 'Seed sample data',
    seed_sample_confirm: 'This will create: 1 property + 1 unit + 1 customer + 1 lease. Continue?',
    seed_sample_success: 'Sample lease (with property, unit, and customer) created successfully.',
    seed_sample_noop: 'You already have leases. Sample data was not added.',
  },

  // Filters
  filters: {
    search_placeholder: 'Search lease code or unit number...',
  },

  // Sign links modal
  sign_modal: {
    title: 'Configure Signers',
    tenant_name: 'Tenant Name',
    owner_name: 'Owner / Landlord Name',
    witness_1: 'Witness 1 (optional)',
    witness_2: 'Witness 2 (optional)',
    generate: 'Generate Sign Links',
    // Fallback signer names sent to the sign-link API when a field is left
    // blank — also surfaced later in the Signature Status Panel.
    default_tenant_name: 'Tenant',
    default_owner_name: 'Owner',
    default_witness_1_name: 'Witness 1',
    default_witness_2_name: 'Witness 2',
  },

  // Signature panel
  signature_panel: {
    title: 'Signatures',
    signed: 'Signed',
    unsigned: 'Not signed yet',
    expired: 'Expired',
    expires_at: 'Expires {{date}}',
    all_signed: 'All parties have signed',
    copy_link: 'Copy link',
    link_copied: 'Link copied!',
    regenerate: 'Regenerate',
    regenerate_title: 'Regenerate Sign Link',
    signer_name: 'Signer Name',
    expires_in_hours: 'Expires in (hours)',
  },

  // Form section headings (EP-86 S2: Essentials/Advanced regroup)
  sections: {
    essentials: 'Essentials',
    advanced_options: 'Advanced options',
    terms: 'Terms',
    documents: 'Documents',
  },

  // Attachments section
  attachments: {
    title: 'Attachments',
    images_section: 'Images',
    files_section: 'Files',
    upload_image: 'Upload Image',
    upload_file: 'Upload File',
  },

  // EP-160 T160-104x: blackout of religion and blood group on an ID-card copy
  id_copy: {
    checkbox: 'This is a copy of an ID card',
    images_only_hint: 'Blackout works on images only (JPEG, PNG, WebP). Documents upload as they are.',
    not_supported: 'Only JPEG, PNG and WebP images can be blacked out. Untick "This is a copy of an ID card" to upload this file as it is.',
    title: 'Cover religion and blood group',
    instructions: 'Drag the black boxes over the religion and blood-group lines. Resize with the corner handle, or use the arrow keys (with Shift to resize). Only the covered image is uploaded.',
    preview_alt: 'ID card copy',
    box_label: 'Black box {{number}}',
    remove_box: 'Remove black box {{number}}',
    add_box: 'Add box',
    use_blacked_out: 'Use blacked-out image',
    failed: 'The image could not be read or exported. Try another image.',
  },

  // Sign page (public signing page)
  sign_page: {
    title: 'Lease Agreement',
    lease_signing: 'Lease Signing',
    lease_details: 'Lease Details',
    labels: {
      property: 'Property',
      unit: 'Unit',
      tenant: 'Tenant',
      period: 'Period',
      rent: 'Monthly Rent',
      deposit: 'Deposit',
      // EP-156 (T156-009/T156-010, §3.3/S9) — shown only when set.
      move_in_fee: 'Move-in Fee',
      keycard_deposit: 'Key-card Deposit',
    },
    signing_as: 'Signing as',
    your_signature: 'Your Signature',
    draw_instruction: 'Draw your signature in the box below using your finger or mouse.',
    clear: 'Clear',
    submit_signature: 'Submit Signature',
    agree_terms: 'By submitting your signature, you agree to the lease agreement terms.',
    states: {
      success_title: 'Signature Submitted',
      success_message: 'Your signature has been recorded successfully.',
      all_signed: 'All parties have signed. The lease agreement is now complete.',
      waiting_others: 'Waiting for other parties to sign.',
      already_signed_title: 'Already Signed',
      already_signed_message: 'You have already signed this lease agreement.',
      signed_on: 'Signed on {{date}}',
      expired_title: 'Link Expired',
      expired_message: 'This signing link has expired. Please contact your property manager for a new link.',
      invalid_title: 'Invalid Link',
      invalid_message:
        'This signing link is not valid. Please contact your property manager for a new link.',
    },
    errors: {
      generic: 'Something went wrong. Please try again.',
      network: 'Network error. Please check your connection and try again.',
      changed: 'This lease was just updated by another signer. Press sign again. Your signature is preserved.',
    },
  },

  // Messages
  messages: {
    delete_confirm: 'Delete this lease? This action cannot be undone.',
    delete_title: 'Delete Lease',
    end_confirm: 'End this lease? The lease will be marked as ended.',
    // EP-156 (T156-010, §3.3/S7) — shown only when the lease has no renewal
    // (has_renewal === false) and at least one of move_in_fee/keycard_deposit
    // is set; a missing or true has_renewal falls back to end_confirm above.
    end_confirm_with_expense: 'End this lease? The lease will be marked as ended, and an expense of {{amount}} will be created (move-in fee + key-card deposit refund).',
    end_success: 'Lease ended',
    end_success_with_expense: 'Lease ended, created a pending expense',
    // EP-162 item 11 follow-up 1: Renew creates a draft lease for the same room and tenant.
    renew_confirm: 'Renew this lease? A new draft lease is created for the same room and tenant, with the same rent and deposit. You can edit it before it is signed.',
    renew_success: 'Renewal created as a draft',
    creating: 'Creating lease and generating PDF...',
    created: 'Lease created successfully',
    updated: 'Lease updated successfully',
    deleted: 'Lease deleted successfully',
    signed: 'Lease signed successfully',
    pdf_regenerated: 'The stored PDF was missing, so it was regenerated from the lease template. Any signatures it carried are not recoverable.',
    not_found: 'Lease not found',
    ongoing: 'Ongoing',
    unknown: 'Unknown',
    per_month: '/month',
    property_default: 'Property default: {{hint}}',
    deposit_months: '{{count}} month(s) of rent',
    deposit_fixed: '฿{{amount}} fixed',
    detail_description: 'Lease agreement started {{date}}',
    back_to_leases: 'Back to Leases',
    no_lease_template: 'No lease template found. Please configure one in Settings → Templates.',
    preview_requires_fields: 'Select a unit, a customer, and a start date to preview the PDF.',
  },

  // Top-level error messages
  errors: {
    lease_template_missing: 'No lease document template found. Please configure a lease template in Settings → Templates.',
    unit_required: 'Select an available unit to continue.',
    pdf_missing_needs_admin: 'The stored PDF is missing. Ask an admin or owner to open this lease so it can be regenerated.',
    deposit_invoice_exists: 'A deposit invoice already exists for this lease.',
    // T149-079 F8 (MON-7): POST /api/leases/:id/deposit_invoice ("Bill deposit")
    // returns this once a paid booking fee and/or a deposit held before
    // FlowRent already cover the full deposit, leaving nothing to bill.
    deposit_already_covered: 'The deposit is already fully covered. There is nothing left to bill.',
    // T149-076 (MON-7 A/C): POST /api/leases/:id/deposit_held typed 422 codes.
    deposit_already_held: 'This lease already has a deposit held before FlowRent recorded.',
    deposit_already_collected: "This lease's deposit is already fully collected.",
    // EP-136 (T136-024): cancel_booking + extend-expiry typed 422 codes.
    not_a_booking: 'This lease is not a booking.',
    already_ended: 'This lease has already ended.',
    disposition_invalid: 'Invalid booking-fee disposition.',
    expiry_in_past: 'The reservation expiry must be in the future.',
    // T149-010 (MON-1 Q2): the booking forfeiture journal entry could not be
    // posted. The whole cancellation was rolled back, so the lease is still
    // a draft booking and nothing about it changed.
    booking_forfeiture_journal_failed: 'Could not post the booking forfeiture journal entry. The cancellation was not saved. Please try again or contact support.',
    // T158-009: DELETE /api/leases/:id typed 422 codes.
    lease_not_draft: 'Only a draft lease can be deleted.',
    lease_has_invoices: 'This lease has invoices and cannot be deleted.',
  },

  // EP-136 (T136-024): the Cancel booking / Extend expiry actions on the
  // unit-detail reservation card (UnitBookingCard).
  booking: {
    cancel: {
      action: 'Cancel booking',
      title: 'Cancel booking',
      deposit_warning: 'Cancelling ends the draft lease and releases the unit. Choose what happens to the booking fee already collected.',
      forfeit_label: 'Forfeit the booking fee',
      forfeit_hint: 'Keep it as revenue. The unit is released and the draft lease ends.',
      refund_label: 'Refund the booking fee',
      refund_hint: 'Record a refund to the customer. The unit is released and the draft lease ends.',
      revenue_message: 'This cancels the booking and ends the draft lease. The reservation fee already collected is non-refundable revenue and will not be reversed.',
      unpaid_message: 'This cancels the booking, ends the draft lease, and removes the unpaid booking invoice. The unit becomes available again.',
      success: 'Booking cancelled',
    },
    extend: {
      action: 'Extend expiry',
      title: 'Extend reservation',
      date_label: 'New expiry date',
      date_required: 'Select a new expiry date',
      date_too_early: 'The new expiry date must be after the current one',
      success: 'Reservation extended',
    },
  },

  // EP-149 T149-081 (MON-7 D): account-wide deposits-held summary near the
  // list header, not per-lease like deposit_panel below.
  deposits_held_summary: {
    label: 'Deposits held',
  },

  // EP-130 (T130-011): deposit panel on lease detail
  deposit_panel: {
    title: 'Deposit',
    amount_label: 'Deposit',
    billed_label: 'Billed',
    unbilled: 'Not yet billed',
    status_paid: 'Paid',
    status_pending: 'Awaiting payment',
    bill_success: 'Deposit invoice created',
    // EP-136 (T136-025): the booking-fee row + the "billed" row's relabel +
    // the total-collected row, shown only when the lease carries a booking.
    booking_fee_label: 'Booking fee',
    remainder_label: 'Remainder billed',
    collected_of: '{{collected}} of {{total}}',
    total_collected_label: 'Total collected',
    status_full: 'Full',
    status_short: 'Short',
    // EP-149 T149-079 (MON-7 C): record a deposit held from before FlowRent.
    record_held_before_flowrent: 'Record deposit held',
    held_before_flowrent_hint: 'Use this when the deposit was already held before starting to use FlowRent.',
    held_before_flowrent_amount_label: 'Amount held',
    held_before_flowrent_date_label: 'Date held',
    held_before_flowrent_success: 'Deposit held recorded',
  },

  // EP-130 (T130-012): settlement card on lease detail
  settlement_card: {
    title: 'Deposit Settlement',
    never_collected_title: 'No deposit collected to settle',
    never_collected_description: 'This lease has no paid deposit invoice, so there is nothing to settle.',
    collected_label: 'Collected',
    deductions_label: 'Deductions',
    add_work_order: 'Add move-out work order',
    record_deduction: 'Record a deduction',
    no_deductions: 'No deductions yet',
    expense_fallback_label: 'Expense',
    returnable_label: 'Returnable',
    return_deposit: 'Return Deposit',
    shortfall_message: 'Damages exceed the deposit by {{amount}}',
    shortfall_line_description: 'Deposit shortfall',
    bill_shortfall: 'Bill the difference',
    returned_label: 'Returned',
    remaining_label: 'Remaining',
    cleared_badge: 'Cleared',
  },

  // Wizard preview
  preview: {
    draft_code: 'DRAFT-PREVIEW',
  },

  // EP85-P3-05: tenant vehicles card on lease detail
  tenant_vehicles: {
    title: 'Tenant Vehicles',
    title_with_count: 'Tenant Vehicles ({{count}})',
    empty: 'No vehicles registered yet',
    edit_vehicle: 'Edit vehicle',
    add_vehicle: '+ Add vehicle',
    add_vehicle_modal_title: 'Add Tenant Vehicle',
    photo_placeholder: 'No photo',
  },

  // EP85-P3-06: LPR events card on lease detail
  lpr_events_card: {
    title: 'LPR Events',
    empty: 'No LPR events for these vehicles yet',
    open_log: 'Open LPR log →',
    candidate_id: 'Candidate ID',
    score: 'Score',
    direction: 'Direction',
    status: 'Status',
  },

  // EPV-11: vehicle reassignment section on ended leases
  reassign_vehicles: {
    title: 'Vehicles Needing Action',
    description: 'These vehicles are still linked to this ended lease. Please archive, reassign, or convert each one.',
    archive: 'Archive',
    reassign: 'Reassign',
    convert_to_external: 'Convert to External',
    confirm_archive: 'Archive this vehicle? It will be unlinked from all leases and customers.',
    reassign_to_lease: 'Reassign to Lease',
    assign_customer: 'Assign Customer',
    confirm: 'Confirm',
  },
}
